<script lang="ts" setup>
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { Breadcrumbs, LoadingSpinner } from "@/components";
import { useDocumentQuery } from "@/graphql/operations";
import PresentationAnalytics from "@/views/document_editor/presentation_analytics/PresentationAnalytics.vue";
import { hasPresentationAnalyticsAccess } from "@/views/document_editor/presentation_analytics/presentationAnalytics";
import { readPresentationAnalyticsSelectorOrigin } from "./presentationAnalyticsNavigation";

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

const documentId = computed(() => route.params.documentId as string);
const selectorOrigin = readPresentationAnalyticsSelectorOrigin(
  window.history.state,
);
const selectorPath =
  selectorOrigin?.selectorPath ??
  router.resolve({ name: "PresentationAnalyticsSelector" }).fullPath;

const { data, error, fetching, stale } = useDocumentQuery({
  variables: computed(() => ({ id: documentId.value })),
  requestPolicy: "network-only",
});

const document = computed(() => {
  const loadedDocument = data.value?.document ?? null;
  return loadedDocument?.id === documentId.value ? loadedDocument : null;
});

const canAccessAnalytics = computed(() =>
  hasPresentationAnalyticsAccess({
    hasCurrentAccessResult: !fetching.value && !stale.value && !error.value,
    hasPresentationAnalytics: store.getters.user.hasPresentationAnalytics(),
    mayAdministrateRoot: Boolean(
      document.value?.rootWorkspace?.myPrivileges?.mayAdministrate,
    ),
    deletedAt: document.value?.deletedAt,
    mimeType: document.value?.mimeType,
    numNormalPages: document.value?.numNormalPages,
  }),
);

const breadcrumbItems = computed(() => [
  {
    text: t("presentationAnalyticsSelector.breadcrumb.analytics"),
    href: "/u/analytics",
  },
  {
    text: t("presentationAnalyticsSelector.title"),
    href: selectorPath,
  },
  {
    text: document.value?.displayTitle,
  },
]);

function backToResults() {
  if (selectorOrigin) {
    router.back();
    return;
  }

  void router.push({ name: "PresentationAnalyticsSelector" });
}
</script>

<template>
  <main class="pane page_content presentation_analytics_detail">
    <Breadcrumbs :items="breadcrumbItems" />

    <button
      type="button"
      class="back_to_results"
      data-test="presentation-analytics-back-to-results"
      @click="backToResults"
    >
      <span class="material-symbols-outlined" aria-hidden="true">
        arrow_back
      </span>
      {{ $t("presentationAnalyticsSelector.backToResults") }}
    </button>

    <div v-if="fetching || stale" class="detail_state">
      <LoadingSpinner :color="'var(--classic-text, black)'" />
    </div>

    <PresentationAnalytics
      v-else-if="document && canAccessAnalytics"
      :validated-document="document"
      data-test="presentation-analytics-detail-dashboard"
    />

    <div v-else class="detail_state" role="alert">
      {{ $t("presentationAnalyticsSelector.empty.unavailable") }}
    </div>
  </main>
</template>

<style scoped>
.presentation_analytics_detail {
  box-sizing: border-box;
  min-height: 100%;
  padding-bottom: 4rem;
}

.back_to_results {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0.75rem 0 0.25rem;
  padding: 0.35rem 0;
  border: 0;
  background: transparent;
  color: var(--classic-text-muted, #736c64);
  cursor: pointer;
  font: inherit;
}

.back_to_results:hover,
.back_to_results:focus-visible {
  color: inherit;
  text-decoration: underline;
}

.back_to_results .material-symbols-outlined {
  font-size: 1.1rem;
}

.detail_state {
  display: flex;
  min-height: 18rem;
  align-items: center;
  justify-content: center;
  color: var(--classic-text-muted, #736c64);
  text-align: center;
}
</style>
